import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { HttpService } from '../core/http.service';
import { WidgetService } from './widget.service';

/**
 * Validation of the widget's `api_key` query parameter against `GET <API_URL>/api/isvalidkey?key=`.
 *
 * The endpoint answers in the backend's usual envelope — `{ "failed": { "valid": false, "key": … } }`
 * for a rejected key, `{ "success": { "valid": true, … } }` for an accepted one. Only an explicit
 * `valid: true` loads the widget: a missing key, a rejected key, an unexpected answer, a network
 * error or a timeout all resolve to `false`, so a broken key never silently loads the widget.
 */
@Injectable({ providedIn: 'root' })
export class WidgetAuthService {

  private readonly widgetService = inject(WidgetService);
  private readonly httpService = inject(HttpService);

  /** One validation per page load, shared by every navigation to the widget route. */
  private result$: Observable<boolean> | null = null;

  public validate(): Observable<boolean> {
    if (!this.result$) {
      this.result$ = this.check().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.result$;
  }

  private check(): Observable<boolean> {
    const apiKey = this.widgetService.getApiKey();
    if (!apiKey) {
      return of(false);
    }
    return this.httpService.validateWidgetApiKey(apiKey).pipe(
      map((resp) => isValidKeyResponse(resp)),
      catchError(() => of(false)),
    );
  }
}

/** `{ success: { valid: true } }` (or a bare `{ valid: true }`); anything else is a rejection. */
function isValidKeyResponse(resp: any): boolean {
  if (resp?.failed) return false;
  return resp?.success?.valid === true || resp?.valid === true;
}
